import { useEffect, useRef } from 'react';
import { instance } from '@/services/api';
import { useRefreshTokenMutation } from './authService';

/**
 * 401 시 1회만 refresh 후 재시도. 재시도도 401이면 로그아웃(무한 refresh ↔ me 루프 방지).
 * App 렌더마다 인터셉터가 중복 등록되지 않도록 useEffect 안에서만 등록한다.
 */
export function useAxiosAuth(us) {
    const refreshMutation = useRefreshTokenMutation(us);
    const mutateAsyncRef = useRef(refreshMutation.mutateAsync);
    mutateAsyncRef.current = refreshMutation.mutateAsync;

    const usRef = useRef(us);
    usRef.current = us;

    useEffect(() => {
        let refreshInFlight = null;

        const runRefresh = () => {
            if (!refreshInFlight) {
                refreshInFlight = mutateAsyncRef.current().finally(() => {
                    refreshInFlight = null;
                });
            }
            return refreshInFlight;
        };

        const reqId = instance.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('accessToken');
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        const resId = instance.interceptors.response.use(
            (res) => res,
            async (err) => {
                const originalRequest = err.config;
                const status = err.response?.status;

                if (status !== 401 || !originalRequest) {
                    return Promise.reject(err);
                }

                const reqUrl = String(originalRequest.url || '');
                if (reqUrl.includes('/auth/refresh-token')) {
                    return Promise.reject(err);
                }

                if (originalRequest._authRetry) {
                    usRef.current?.logout?.();
                    return Promise.reject(err);
                }

                originalRequest._authRetry = true;

                try {
                    const response = await runRefresh();
                    originalRequest.headers = originalRequest.headers || {};
                    originalRequest.headers.Authorization = `Bearer ${response.data.accessToken}`;
                    return instance(originalRequest);
                } catch (refreshErr) {
                    return Promise.reject(refreshErr);
                }
            }
        );

        return () => {
            instance.interceptors.request.eject(reqId);
            instance.interceptors.response.eject(resId);
        };
    }, []);
}

export default useAxiosAuth;
